import type { ReactNode } from "react";
import { PULSE_VIEW_LABELS, type PulseViewKind } from "@/lib/dashboard/pulse-views";
import { Card } from "@/components/ui/card";

function Placeholder({ className = "" }: { className?: string }) {
  return <div className={`skeleton-shimmer max-w-full rounded ${className}`} />;
}

function HeadlinePlaceholder() {
  return <div className="space-y-4">
    <Placeholder className="h-3 w-44" />
    <div className="space-y-2"><Placeholder className="h-8 w-5/6" /><Placeholder className="h-8 w-3/5" /></div>
    <Placeholder className="h-4 w-52" />
  </div>;
}

function MetricsPlaceholder({ count = 3 }: { count?: number }) {
  return <div className={`grid gap-5 border-y border-spark-hairline py-6 ${count === 3 ? "sm:grid-cols-3" : "grid-cols-2 lg:grid-cols-6"}`}>
    {Array.from({ length: count }, (_, i) => <div key={i} className="min-w-0 space-y-3">
      <Placeholder className="h-3 w-24" /><Placeholder className="h-8 w-28" /><Placeholder className="h-3 w-36" />
    </div>)}
  </div>;
}

function SupportingPlaceholder() {
  return <div className="space-y-6 border-t border-spark-hairline pt-6">
    <Placeholder className="h-4 w-40" /><Placeholder className="h-4 w-36" />
  </div>;
}

function BriefPlaceholder({ clientAudience = false }: { clientAudience?: boolean }) {
  return <div aria-hidden="true" className="space-y-8">
    <HeadlinePlaceholder />
    <MetricsPlaceholder />
    {!clientAudience && <Card padding="lg" className="space-y-5 sm:p-8">
      <Placeholder className="h-5 w-28 rounded-full" />
      <Placeholder className="h-7 w-2/3" />
      <div className="space-y-2"><Placeholder className="h-4 w-full" /><Placeholder className="h-4 w-4/5" /></div>
      <Placeholder className="h-3 w-44" />
      <Placeholder className="h-9 w-52 rounded-md" />
    </Card>}
    <div>
      <Placeholder className="mb-2 h-4 w-36" />
      {[0, 1].map(i => <div key={i} className="space-y-3 border-b border-spark-hairline py-5">
        <Placeholder className="h-5 w-2/3" /><Placeholder className="h-4 w-5/6" />
      </div>)}
    </div>
    <SupportingPlaceholder />
  </div>;
}

function QueuePlaceholder() {
  return <div aria-hidden="true" className="space-y-7">
    <div className="flex flex-wrap items-start justify-between gap-4"><Placeholder className="h-4 w-3/4" /><Placeholder className="h-5 w-24 rounded-full" /></div>
    <div className="flex w-fit max-w-full gap-2 rounded-lg bg-spark-lightest-gray p-1">
      {[0, 1, 2].map(i => <div key={i} className={`min-w-0 rounded-md px-3 py-3 ${i === 0 ? "bg-spark-surface" : ""}`}><Placeholder className="h-4 w-16" /></div>)}
    </div>
    <Card padding="none" className="grid min-w-0 overflow-hidden xl:grid-cols-[minmax(260px,0.8fr)_minmax(0,1.5fr)]">
      <div className="space-y-3 border-b border-spark-hairline p-5 xl:hidden">
        <Placeholder className="h-4 w-32" /><Placeholder className="h-11 w-full rounded-md" />
      </div>
      <div className="hidden min-w-0 border-r border-spark-hairline p-3 xl:block">
        <Placeholder className="mx-3 my-3 h-3 w-36" />
        {Array.from({ length: 5 }, (_, i) => <div key={i} className={`space-y-3 rounded-lg p-4 ${i === 0 ? "bg-spark-hover" : ""}`}>
          <Placeholder className="h-3 w-28" /><Placeholder className="h-4 w-5/6" /><Placeholder className="h-3 w-36" />
        </div>)}
      </div>
      <div className="min-w-0 space-y-6 p-5 sm:p-8">
        <Placeholder className="h-5 w-28 rounded-full" />
        <Placeholder className="h-7 w-4/5" />
        <div className="space-y-2"><Placeholder className="h-4 w-full" /><Placeholder className="h-4 w-3/4" /></div>
        <div className="grid grid-cols-2 gap-5 border-y border-spark-hairline py-5">
          {[0, 1].map(i => <div key={i} className="min-w-0 space-y-3"><Placeholder className="h-3 w-28" /><Placeholder className="h-8 w-20" /></div>)}
        </div>
        <div className="space-y-3"><Placeholder className="h-4 w-36" /><Placeholder className="h-4 w-5/6" /><Placeholder className="h-4 w-3/4" /><Placeholder className="h-4 w-4/5" /></div>
        <Placeholder className="h-9 w-40 rounded-md" />
      </div>
    </Card>
    <SupportingPlaceholder />
  </div>;
}

function ClassicPlaceholder() {
  return <div aria-hidden="true" className="space-y-6">
    <div className="bg-brand-dark space-y-4 rounded-2xl px-6 py-5">
      <Placeholder className="h-3 w-28" /><Placeholder className="h-7 w-5/6" /><Placeholder className="h-3 w-full" />
    </div>
    <Card className="space-y-3"><Placeholder className="h-4 w-40" /><Placeholder className="h-4 w-5/6" /><Placeholder className="h-4 w-2/3" /></Card>
    <MetricsPlaceholder count={6} />
    {[0, 1].map(i => <Card key={i} className="space-y-5">
      <Placeholder className="h-5 w-44" />
      <div className="grid gap-5 md:grid-cols-2">{[0, 1].map(col => <div key={col} className="space-y-3"><Placeholder className="h-3 w-28" /><Placeholder className="h-16 w-full rounded-lg" /><Placeholder className="h-16 w-full rounded-lg" /></div>)}</div>
    </Card>)}
  </div>;
}

/** Shared by route, section, and streaming fallbacks. It has no dependency on
 * the stream context: only PulseCanvas supplies the live progress slot. */
export function PulseLoading({ view, periodLabel, progress, audience }: {
  view: PulseViewKind;
  audience?: "team" | "client";
  periodLabel?: string;
  progress?: ReactNode;
}) {
  return <section data-pulse-loading={view} aria-label={`Loading ${PULSE_VIEW_LABELS[view]}`} className={`space-y-5 ${view !== "classic" ? "mx-auto max-w-4xl" : ""}`}>
    {progress ?? <p role="status" className="text-sm text-spark-gray">Preparing {PULSE_VIEW_LABELS[view]}…{periodLabel && <span className="ml-2">{periodLabel}</span>}</p>}
    <div aria-busy="true">
      {view === "brief" ? <BriefPlaceholder clientAudience={audience === "client"} /> : view === "queue" ? <QueuePlaceholder /> : <ClassicPlaceholder />}
    </div>
  </section>;
}

/** Reserve the same title/controls and audience spacing while server-side
 * account and permission data is loading. No interactive-looking real controls. */
export function PulsePageLoading({ view }: { view: PulseViewKind }) {
  return <div data-pulse-page-loading={view}>
    <div aria-hidden="true" className="mb-8 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
      <Placeholder className="h-9 w-80" />
      <div className="flex max-w-full flex-wrap gap-2"><Placeholder className="h-9 w-40 rounded-md" /><Placeholder className="h-9 w-32 rounded-md" /><Placeholder className="h-9 w-28 rounded-md" /></div>
    </div>
    <div aria-hidden="true" className="mb-4 space-y-4">
      <Card padding="none" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"><Placeholder className="h-4 w-60" /><div className="flex max-w-full flex-wrap gap-2"><Placeholder className="h-5 w-28 rounded-full" /><Placeholder className="h-5 w-32 rounded-full" /></div></Card>
      <Placeholder className="h-10 w-52 rounded-lg" />
    </div>
    <PulseLoading view={view} />
  </div>;
}
